import { useCallback, useEffect, useState } from "react";
import type { Oekt } from "../../state/session";
import * as oppsett from "../../state/oppsett";
import * as fagfunksjoner from "../../state/fagfunksjoner";
import * as logo from "../../state/logo";
import { IkkeDin, Topp } from "./deler";

/**
 * Oppsett · Produksjon.
 *
 * Navnet, dagene, teamene og de som venter på å slippes inn. Prodleder og
 * produsent.
 *
 * ========  EN VANLIG SIDE, IKKE EN MODUS  ========
 *
 * Oppsettet lå før på én flate som dekket pageren, og den eneste veien
 * tilbake var en «Ferdig»-knapp. Brukeren 27.10.2026: «Slik det er nå, så
 * må jeg trykke ferdig for å komme tilbake til arbeidsflaten.»
 *
 * Nå er de tre oppsettsflatene sider som alle andre — man blar vekk fra
 * dem — og den som er ferdig med dem slår dem av under «Sider» i menyen.
 * Det er også det riktige stedet for valget: oppsett er noe man gjør én
 * gang, og en fane man blar forbi hver dag for noe man gjorde i august er
 * en fane i veien.
 */
export function OppsettProduksjon({ oekt }: { readonly oekt: Oekt }): JSX.Element {
  if (!oppsett.minePunkter(oekt.member.role, oekt.member.extraRoles ?? []).includes("produksjon")) {
    return <IkkeDin />;
  }
  return (
    <div className="flate flate--oppsett">
      <div className="flate__innhold">
        <Topp
          tittel="Produksjon"
          under={`${oekt.production.name} · navn, dager, team og tilganger`}
        />
        <div className="oppsett__rutenett">
          <ProduksjonKort oekt={oekt} />
          <LogoKort oekt={oekt} />
          <DagerKort oekt={oekt} />
          <TeamKort oekt={oekt} />
          <TilgangKort oekt={oekt} />
        </div>
      </div>
    </div>
  );
}

function ProduksjonKort({ oekt }: { readonly oekt: Oekt }): JSX.Element {
  const [navn, setNavn] = useState(oekt.production.name);
  const [slutt, setSlutt] = useState(oekt.production.endDateIso);
  const [feil, setFeil] = useState<string | null>(null);

  const lagre = useCallback((felt: Record<string, unknown>): void => {
    void (async () => {
      const svar = await oppsett.lagreProduksjon(oekt.production.id, felt);
      setFeil(svar.ok ? null : svar.feil ?? "Ble ikke lagret.");
    })();
  }, [oekt.production.id]);

  return (
    <div className="loggkort">
      <div className="loggkort__hode"><span className="loggkort__navn">Produksjonen</span></div>
      <div className="oppsett__felt">
        <label className="oppsett__etikett" htmlFor="opp-navn">Navn</label>
        <input
          id="opp-navn" className="deltakere__felt" value={navn}
          onChange={(e) => setNavn(e.target.value)}
          onBlur={() => lagre({ name: navn.trim() })}
        />
      </div>
      <div className="oppsett__felt">
        <label className="oppsett__etikett" htmlFor="opp-slutt">Siste dag</label>
        <input
          id="opp-slutt" type="date" className="deltakere__felt" value={slutt}
          onChange={(e) => setSlutt(e.target.value)}
          onBlur={() => lagre({ end_date: slutt })}
        />
      </div>
      {/* Hvorfor datoen betyr noe. Den er ikke administrasjon — den styrer
          når loggen slettes fra private telefoner. */}
      <p className="merknad oppsett__merknad">
        Siste dag styrer når den lokale loggen slettes fra crewets telefoner.
        Den kan forlenges, og forlengelsen skrives til revisjonsloggen.
      </p>
      {feil === null ? null : <p className="innlogging__feil">{feil}</p>}
    </div>
  );
}

/**
 * Logoene.
 *
 * ========  TO, OG DEN ENE GÅR FORAN DEN ANDRE  ========
 *
 * Brukeren 29.10.2026: «Hvis det blir lastet opp en logo på et selskap, så
 * byttes logoen vi har i appen og på nettsiden til logoen til selskapet,
 * hvis det lastes opp logo til prosjektet er det prosjektslogoen som
 * vises.» Rekkefølgen står i flata, ikke bare i koden — ellers ser det ut
 * som en feil at selskapslogoen forsvant.
 *
 * ========  FILVELGEREN ER SKJULT, IKKE BORTE  ========
 *
 * `input[type=file]` lar seg ikke stile, så knappen er etiketten. Feltet
 * ligger inne i den og er skjult med `.logo__velger` — ikke med
 * `display: none`, som tar det ut av tastaturrekkefølgen.
 */
function LogoKort({ oekt }: { readonly oekt: Oekt }): JSX.Element {
  const [data, setData] = useState<logo.Logoer | null>(null);
  const [lenker, setLenker] = useState<{
    selskap: string | null; produksjon: string | null;
  }>({ selskap: null, produksjon: null });
  const [jobber, setJobber] = useState<logo.Slag | null>(null);
  const [feil, setFeil] = useState<string | null>(null);

  const les = useCallback((): void => {
    void (async () => {
      const d = await logo.hent(oekt.production.id);
      setData(d);
      setLenker({
        selskap: await logo.lenke(d.selskapLogo),
        produksjon: await logo.lenke(d.produksjonLogo),
      });
    })();
  }, [oekt.production.id]);
  useEffect(les, [les]);

  const velg = useCallback((slag: logo.Slag, fil: File | null): void => {
    if (fil === null || data === null) return;
    const eier = slag === "selskap" ? data.selskapId : oekt.production.id;
    if (eier === null) { setFeil("Fant ikke selskapet."); return; }

    const galt = logo.sjekkFil(fil);
    if (galt !== null) { setFeil(galt); return; }

    setFeil(null);
    setJobber(slag);
    void (async () => {
      const forrige = slag === "selskap" ? data.selskapLogo : data.produksjonLogo;
      const svar = await logo.lastOpp(slag, eier, fil, forrige);
      setJobber(null);
      if (!svar.ok) { setFeil(svar.feil ?? "Ble ikke lastet opp."); return; }
      les();
    })();
  }, [data, oekt.production.id, les]);

  const fjern = useCallback((slag: logo.Slag): void => {
    if (data === null) return;
    const eier = slag === "selskap" ? data.selskapId : oekt.production.id;
    if (eier === null) return;
    setJobber(slag);
    void (async () => {
      const sti = slag === "selskap" ? data.selskapLogo : data.produksjonLogo;
      const svar = await logo.fjern(slag, eier, sti);
      setJobber(null);
      if (!svar.ok) { setFeil(svar.feil ?? "Ble ikke fjernet."); return; }
      les();
    })();
  }, [data, oekt.production.id, les]);

  const rad = (slag: logo.Slag, tittel: string, under: string): JSX.Element => {
    const url = slag === "selskap" ? lenker.selskap : lenker.produksjon;
    const har = (slag === "selskap" ? data?.selskapLogo : data?.produksjonLogo)
      != null;
    return (
      <div className="logo__rad">
        <div className="logo__rute">
          {url === null
            ? <span className="logo__tom" aria-hidden="true" />
            : <img className="logo__bilde" src={url} alt="" />}
        </div>
        <div className="logo__tekst">
          <span className="logo__tittel">{tittel}</span>
          <span className="merknad">{under}</span>
        </div>
        <div className="logo__knapper">
          <label className="synk__knapp logo__knapp">
            {jobber === slag ? "Laster…" : har ? "Bytt" : "Last opp"}
            <input
              className="logo__velger"
              type="file"
              accept="image/png,image/jpeg,image/svg+xml,image/webp"
              disabled={jobber !== null}
              onChange={(e) => {
                velg(slag, e.target.files?.[0] ?? null);
                e.target.value = "";
              }}
            />
          </label>
          {!har ? null : (
            <button
              type="button"
              className="synk__knapp logo__knapp"
              disabled={jobber !== null}
              onClick={() => fjern(slag)}
            >
              Fjern
            </button>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="loggkort">
      <div className="loggkort__hode">
        <span className="loggkort__navn">Logo</span>
      </div>
      {rad("produksjon", oekt.production.name,
        "Vises i appen, på nettflata og øverst på dokumentene.")}
      {rad("selskap", data?.selskapsnavn ?? "Selskapet",
        "Gjelder alle produksjonene i selskapet.")}
      <p className="merknad oppsett__merknad">
        Produksjonens logo går foran selskapets. Er ingen av dem satt, står
        Infoflyt-merket. PNG, JPG, SVG eller WebP, høyst 2 MB — og{" "}
        <strong>PNG uten bakgrunn</strong> gir best resultat: logoen står mot
        lys flate her, mot mørk i blackout, og mot hvitt papir øverst på
        rapporten. En hvit firkant rundt merket synes alle tre stedene.
      </p>
      {feil === null ? null : <p className="innlogging__feil">{feil}</p>}
    </div>
  );
}

function DagerKort({ oekt }: { readonly oekt: Oekt }): JSX.Element {
  const [dager, setDager] = useState<readonly oppsett.Dag[]>([]);
  const [dag, setDag] = useState("");
  const [dato, setDato] = useState("");
  const [feil, setFeil] = useState<string | null>(null);

  const les = useCallback((): void => {
    void (async () => setDager(await oppsett.hentDager(oekt.production.id)))();
  }, [oekt.production.id]);
  useEffect(les, [les]);

  return (
    <div className="loggkort">
      <div className="loggkort__hode">
        <span className="loggkort__navn">Opptaksdager</span>
        <span className="loggkort__lag">{dager.length}</span>
      </div>
      {dager.map((d) => (
        <div key={d.dag} className="deltakere__rad">
          <span className="deltakere__kort mono">Dag {d.dag}</span>
          <span className="deltakere__navn">{d.dato}</span>
          <button
            type="button" className="deltakere__ut"
            onClick={() => { void oppsett.fjernDag(oekt.production.id, d.dag).then(les); }}
          >
            Fjern
          </button>
        </div>
      ))}
      <form
        className="deltakere__ny"
        onSubmit={(e) => {
          e.preventDefault();
          void (async () => {
            const svar = await oppsett.leggTilDag(oekt.production.id, dag, dato);
            if (!svar.ok) { setFeil(svar.feil ?? "Dagen ble ikke lagret."); return; }
            setFeil(null); setDag(""); setDato(""); les();
          })();
        }}
      >
        <input
          className="deltakere__felt deltakere__felt--kort mono" value={dag}
          onChange={(e) => setDag(e.target.value)} placeholder="7" aria-label="Dagnummer"
        />
        <input
          className="deltakere__felt" type="date" value={dato}
          onChange={(e) => setDato(e.target.value)} aria-label="Dato"
        />
        <button
          type="submit" className="synk__knapp synk__knapp--hoved"
          disabled={dag.trim() === "" || dato === ""}
        >
          Legg til
        </button>
      </form>
      {/* Nummer og dato følger ikke hverandre av seg selv. En dag kan utgå
          uten at nummereringen forskyves, og «7B» finnes. */}
      <p className="merknad oppsett__merknad">
        Nummeret er produksjonens eget og trenger ikke følge datoen — «7B»
        er en gyldig dag. Dager før opptaksdag 1 skrives 0, −1, −2.
      </p>
      {feil === null ? null : <p className="innlogging__feil">{feil}</p>}
    </div>
  );
}

function TeamKort({ oekt }: { readonly oekt: Oekt }): JSX.Element {
  const [team, setTeam] = useState<readonly { id: string; navn: string }[]>([]);
  const [navn, setNavn] = useState("");
  const [slag, setSlag] = useState<oppsett.Teamslag>("let");
  const [feil, setFeil] = useState<string | null>(null);

  const les = useCallback((): void => {
    void (async () => {
      const { hentTeam } = await import("../../state/team");
      setTeam(await hentTeam(oekt.production.id));
    })();
  }, [oekt.production.id]);
  useEffect(les, [les]);

  return (
    <div className="loggkort">
      <div className="loggkort__hode">
        <span className="loggkort__navn">Team</span>
        <span className="loggkort__lag">{team.length}</span>
      </div>
      {team.map((t) => (
        <div key={t.id} className="deltakere__rad">
          <span className="deltakere__navn">{t.navn}</span>
          <button
            type="button" className="deltakere__ut"
            onClick={() => { void oppsett.fjernTeam(t.id).then(les); }}
          >
            Fjern
          </button>
        </div>
      ))}
      <form
        className="deltakere__ny deltakere__ny--skjema"
        onSubmit={(e) => {
          e.preventDefault();
          if (navn.trim() === "") return;
          void (async () => {
            const svar = await oppsett.leggTilTeam(oekt.production.id, navn, slag);
            if (!svar.ok) { setFeil(svar.feil ?? "Teamet ble ikke lagret."); return; }
            setFeil(null); setNavn(""); les();
          })();
        }}
      >
        <input
          className="deltakere__felt" value={navn}
          onChange={(e) => setNavn(e.target.value)}
          placeholder="LET 1" aria-label="Teamnavn"
        />
        <select
          className="meny__velger" value={slag}
          onChange={(e) => setSlag(e.target.value as oppsett.Teamslag)}
          aria-label="Slag"
        >
          <option value="let">LET</option>
          <option value="vj">VJ</option>
          <option value="control_room">Kontrollrom</option>
        </select>
        {/* IKKE `disabled`. En knapp som er av uten aa si hvorfor er
            ikke en hindring, den er en gaate — brukeren trykket og
            «det skjedde ingenting». Merknaden under sier hva som
            mangler. */}
        <button type="submit" className="synk__knapp synk__knapp--hoved">
          Legg til
        </button>
      </form>
      {navn.trim() !== "" ? null : (
        <span className="merknad">Gi teamet et navn.</span>
      )}
      <p className="merknad oppsett__merknad">
        Du bestemmer navnet selv — «LET 1», «LET 2», «Kontrollrom A» — og
        velger hva slags team det er. Det er navnet folk ser når de velger
        team; slaget sier bare hva det er for noe.
        <br />
        Teamet samler arbeidet: logg, shotlist og synk fra alle på samme team
        havner i den samme rapporten. Hver enkelt velger selv hvilket team hen
        står på — i menyen, både i appen og her på nett.
      </p>
      {feil === null ? null : <p className="innlogging__feil">{feil}</p>}
    </div>
  );
}

/**
 * Tilgangene.
 *
 * ========  INVITASJONEN ER AVGJØRELSEN  ========
 *
 * Produksjonsleder, produsent og redaktør kan ikke velges av folk selv.
 * Prodleder legger inn adressen og fagfunksjonen her når prosjektet rigges,
 * og raden står klar til personen logger inn.
 *
 * Det snur retningen riktig vei. Godkjenningsmodellen før dette lot folk
 * velge «Redaktør» og så vente på at noen skulle se det — da var tilgangen
 * noe man SØKTE om, og den som rigget produksjonen visste ikke hva hen
 * hadde sagt ja til før noen banket på.
 *
 * FLERE PÅ SAMME FAGFUNKSJON ER POENGET, ikke et unntak: det er ofte mer
 * enn én redaktør. To invitasjoner er bare to rader.
 *
 * ⚠ E-POSTEN SENDES IKKE ENNÅ. Å opprette kontoen krever `service_role`,
 * som aldri kan ligge i nettleseren, og en SMTP-leverandør i EU. Begge
 * kommer med innlogging. Raden står klar til da og binder seg selv ved
 * første innlogging — ingenting må settes opp på nytt.
 */
function TilgangKort({ oekt }: { readonly oekt: Oekt }): JSX.Element {
  const [liste, setListe] = useState<readonly fagfunksjoner.Invitert[]>([]);
  const [fag, setFag] = useState<readonly fagfunksjoner.Fagfunksjon[]>([]);
  const [sender, setSender] = useState<string | null>(null);
  const [epost, setEpost] = useState("");
  const [navn, setNavn] = useState("");
  const [craftId, setCraftId] = useState("");
  const [feil, setFeil] = useState<string | null>(null);

  const les = useCallback((): void => {
    void (async () => setListe(await fagfunksjoner.hentTilganger(oekt)))();
  }, [oekt]);

  useEffect(() => {
    void (async () => {
      const alle = await fagfunksjoner.hentFagfunksjoner();
      setFag(alle.filter((f) => fagfunksjoner.kreverInvitasjon(f.rolle)));
    })();
    les();
  }, [les]);

  /*
   * HVA SOM MANGLER, SAGT HOEYT.
   *
   * Knappen var `disabled` til alle tre feltene var fylt, og brukeren
   * 01.11.2026: «Det virker ogsaa som at inviter-knappen ikke gjoer
   * noe.» Den gjorde ikke noe — den var av, og en knapp som er av uten
   * aa si hvorfor er ikke en hindring, den er en gaate.
   */
  const mangler = epost.trim() === "" ? "Skriv e-postadressen."
    : craftId === "" ? "Velg en fagfunksjon."
    : null;

  const send = useCallback((): void => {
    if (epost.trim() === "" || craftId === "") return;
    void (async () => {
      /* Navnet er valgfritt. Staar det tomt, baerer raden adressen til
         personen selv fyller inn navnet sitt — det er bedre enn aa
         stoppe en invitasjon paa et felt ingen kjenner svaret paa. */
      const detNavnet = navn.trim() === "" ? epost.trim() : navn.trim();
      const kort = detNavnet.split(/\s+/).map((d) => d[0] ?? "").join("").slice(0, 3);
      const svar = await fagfunksjoner.inviter(oekt, epost, craftId, detNavnet, kort);
      if (!svar.ok) { setFeil(svar.feil ?? "Invitasjonen ble ikke lagret."); return; }
      /* Raden foerst, posten etterpaa. Feiler sendingen, staar raden
         igjen med `invite_sent_at = null` og kortet viser «ikke sendt»
         med en knapp — tilgangen henger ikke i e-posten. */
      if (svar.medlemId !== undefined && svar.medlemId !== "") {
        const sendt = await fagfunksjoner.sendInvitasjon(svar.medlemId);
        if (!sendt.ok) setFeil(sendt.feil ?? "Invitasjonen ble ikke sendt.");
        else setFeil(null);
      }
      setEpost(""); setNavn(""); setCraftId(""); les();
    })();
  }, [oekt, epost, navn, craftId, les]);

  const sendPaaNytt = useCallback((id: string): void => {
    setSender(id);
    setFeil(null);
    void (async () => {
      const svar = await fagfunksjoner.sendInvitasjon(id);
      setSender(null);
      if (!svar.ok) { setFeil(svar.feil ?? "Ble ikke sendt."); return; }
      les();
    })();
  }, [les]);

  const leggTil = useCallback((rad: fagfunksjoner.Invitert, nyCraft: string): void => {
    setFeil(null);
    void (async () => {
      const svar = await fagfunksjoner.leggTilFagfunksjon(
        rad.id, nyCraft, rad.ekstraIder);
      if (!svar.ok) { setFeil(svar.feil ?? "Ble ikke lagt til."); return; }
      les();
    })();
  }, [les]);

  const utAvCrewet = useCallback((rad: fagfunksjoner.Invitert): void => {
    setFeil(null);
    void (async () => {
      const svar = rad.ute
        ? await fagfunksjoner.gjeninnsettMedlem(rad.id)
        : await fagfunksjoner.fjernMedlem(rad.id);
      if (!svar.ok) { setFeil(svar.feil ?? "Ble ikke endret."); return; }
      les();
    })();
  }, [les]);

  const fjern = useCallback((rad: fagfunksjoner.Invitert, craft: string): void => {
    setFeil(null);
    void (async () => {
      const svar = await fagfunksjoner.fjernFagfunksjon(
        rad.id, craft, rad.ekstraIder);
      if (!svar.ok) { setFeil(svar.feil ?? "Ble ikke fjernet."); return; }
      les();
    })();
  }, [les]);

  return (
    <div className="loggkort">
      <div className="loggkort__hode">
        <span className="loggkort__navn">Tilganger</span>
        <span className="loggkort__lag">{liste.length}</span>
      </div>

      {liste.map((i) => (
        <Tilgangsrad
          key={i.id} rad={i} fag={fag} sender={sender === i.id}
          onSendPaaNytt={() => sendPaaNytt(i.id)}
          onLeggTil={(c) => leggTil(i, c)}
          onFjern={(c) => fjern(i, c)}
          onUtAvCrewet={() => utAvCrewet(i)}
        />
      ))}

      <span className="oppstart__merke">Inviter en til</span>
      <form
        className="deltakere__ny deltakere__ny--skjema"
        onSubmit={(e) => { e.preventDefault(); send(); }}
      >
        <input
          className="deltakere__felt" type="email" value={epost}
          onChange={(e) => setEpost(e.target.value)}
          placeholder="navn@produksjon.no" aria-label="E-postadresse"
        />
        <input
          className="deltakere__felt" value={navn}
          onChange={(e) => setNavn(e.target.value)}
          placeholder="Navn (valgfritt)" aria-label="Navn"
        />
        <select
          className="meny__velger" value={craftId}
          onChange={(e) => setCraftId(e.target.value)}
          aria-label="Fagfunksjon"
        >
          <option value="">Fagfunksjon …</option>
          {fag.map((f) => <option key={f.id} value={f.id}>{f.navn}</option>)}
        </select>
        <button type="submit" className="synk__knapp synk__knapp--hoved">
          Inviter
        </button>
      </form>
      {mangler === null ? null : (
        <span className="merknad">{mangler}</span>
      )}

      {/* MERKNADEN MAA FOELGE REGLENE.
          Her sto det at bare prodleder, produsent og redaktoer
          inviteres, og at e-posten gikk foerst «naar innlogging er
          paa». Begge deler var utdatert: A-foto og koordinator kom til
          27. og 31.10.2026, og innloggingen har vaert paa siden
          31.10.2026. */}
      <p className="merknad oppsett__merknad">
        Fem fagfunksjoner kommer med invitasjon: produksjonsleder,
        produsent, redaktør, A-foto og koordinator. De kan endre
        produksjonen eller det som sendes ut av den, og de jobber
        hovedsakelig på nettflata. Alle andre velger fagfunksjonen sin
        selv i appen, teknikerne inkludert.
        <br />
        Invitasjonen er en beskjed, ikke en dør: tilgangen henger på
        adressen, og den bindes ved innlogging uansett.
        <br />
        Er personen alt med i produksjonen, er det «Legg til» på raden
        som gjelder — en person har én rad her, og kan ha flere
        fagfunksjoner på den.
      </p>
      {feil === null ? null : <p className="innlogging__feil">{feil}</p>}
    </div>
  );
}

/**
 * Én rad i Tilganger.
 *
 * ========  DEN SIER HVA SOM ALT ER BESTEMT  ========
 *
 * Brukeren 01.11.2026, om valget om fagfunksjon ved siden av en som alt
 * sto som koordinator: «slik den er nå ser det ut som at man skal
 * definere noe som allerede er definert». Derfor står fagfunksjonene
 * som etiketter, og det som kan GJØRES står som «Legg til» — en egen
 * handling, med sitt eget ord.
 */
function Tilgangsrad({
  rad, fag, sender, onSendPaaNytt, onLeggTil, onFjern, onUtAvCrewet,
}: {
  readonly rad: fagfunksjoner.Invitert;
  readonly fag: readonly fagfunksjoner.Fagfunksjon[];
  readonly sender: boolean;
  onSendPaaNytt(): void;
  onLeggTil(craftId: string): void;
  onFjern(craftId: string): void;
  onUtAvCrewet(): void;
}): JSX.Element {
  const [aapen, setAapen] = useState(false);
  /* To trykk, som sletting av en produksjon: foerste armerer, andre
     utfoerer. Armeringen faller av etter fire sekunder. */
  const [armert, setArmert] = useState(false);
  useEffect(() => {
    if (!armert) return;
    const ur = window.setTimeout(() => setArmert(false), 4000);
    return () => window.clearTimeout(ur);
  }, [armert]);

  /* Det hen alt har, staar ikke i lista over hva hen kan faa. */
  const ledige = fag.filter(
    (f) => f.navn !== rad.fagfunksjon && !rad.ekstraIder.includes(f.id));

  return (
    <div className={`tilgang__rad${rad.ute ? " deltakere__rad--ute" : ""}`}>
      <div className="deltakere__rad">
        <span className="deltakere__navn">
          {rad.navn}
          {/* ADRESSEN NAAR DEN SIER NOE ANNET ENN NAVNET.
              To kontoer paa samme menneske er ikke en feil, men uten
              adressen var de to rader man ikke kunne skille — den ene
              med navn, den andre med e-post. Brukeren 01.11.2026. */}
          {rad.epost === "" || rad.epost === rad.navn ? null : (
            <span className="tilgang__epost">{rad.epost}</span>
          )}
        </span>
        <span className="loggkort__lag">
          {[rad.fagfunksjon, ...rad.ekstra].join(" · ")}
        </span>
        {/* TRE TILSTANDER, IKKE TO.
            «venter» sa foer bare at personen ikke hadde logget inn, og
            skjulte det som faktisk kan vaere galt: at beskjeden aldri
            gikk ut. En invitasjon ingen har faatt ser ut som en
            invitasjon noen ignorerer. */}
        {rad.kommetInn ? (
          <span className="loggkort__lag">inne</span>
        ) : !rad.erInvitert ? (
          <span className="loggkort__lag">—</span>
        ) : rad.sendt === null ? (
          <button
            type="button"
            className="synk__knapp tilgang__sendpaanytt"
            disabled={sender}
            onClick={onSendPaaNytt}
          >
            {sender ? "Sender …" : "Ikke sendt · send"}
          </button>
        ) : (
          <span className="loggkort__lag">sendt · venter</span>
        )}

        {/* UT AV CREWET.
            Den som aldri kom inn slettes; den som har vaert inne blir
            staaende og mister tilgangen — loggen maa kunne vise hvem
            som skrev en post. Basen avgjoer hvilket av de to det er,
            og nekter uansett hvis det er den siste lederen. */}
        <button
          type="button"
          className={`deltakere__ut${armert ? " konsoll__armert" : ""}`}
          onClick={() => {
            if (rad.ute) { onUtAvCrewet(); return; }
            if (!armert) { setArmert(true); return; }
            setArmert(false);
            onUtAvCrewet();
          }}
        >
          {rad.ute ? "Sett inn igjen" : armert ? "Ut av crewet?" : "Fjern"}
        </button>
      </div>

      <div className="tilgang__ekstra">
        {rad.ekstra.map((navn, n) => (
          <button
            key={navn}
            type="button"
            className="synk__knapp"
            onClick={() => onFjern(rad.ekstraIder[n] ?? "")}
          >
            {navn} ✕
          </button>
        ))}
        {ledige.length === 0 ? null : !aapen ? (
          <button
            type="button" className="synk__knapp"
            onClick={() => setAapen(true)}
          >
            + Legg til fagfunksjon
          </button>
        ) : (
          <select
            className="meny__velger" value="" autoFocus
            aria-label={`Gi ${rad.navn} en fagfunksjon til`}
            onChange={(e) => {
              if (e.target.value === "") return;
              onLeggTil(e.target.value);
              setAapen(false);
            }}
            onBlur={() => setAapen(false)}
          >
            <option value="">Fagfunksjon …</option>
            {ledige.map((f) => <option key={f.id} value={f.id}>{f.navn}</option>)}
          </select>
        )}
      </div>
    </div>
  );
}

